import { type FC } from 'react';
import {
  Button,
  Flex,
  Heading,
  LoadingDots,
  Text,
} from '@highereducation/hen-ui';
import RecommenderCard from '../Recommender/RecommenderCard';
import { SponsoredResultsPopover } from '../SponsoredResultsPopover';
import type { ProgramSelectProps } from './types';

/**
 * Single unified ProgramSelect step rendered inside the Recommender
 * popup. Matches the EDUN-11769 Figma exactly:
 * - secondary-50 outer container with rounded border + 24px gap
 * - personalized header (Heading) + body line (Text)
 * - up to 2 RecommenderCards side-by-side (stacked on mobile)
 * - footer with SponsoredResultsPopover and "Skip to all results" CTA
 *
 * The card body itself is the hen-ui composed `RecommenderCard`,
 * which carries forward the existing recommender eventing semantics
 * (ProductLoaded / ProductViewed / ProductClicked at
 * `EventingLocation.POPUP`).
 */
const ProgramSelect: FC<ProgramSelectProps> = ({
  results,
  onCloseExperience,
  loading,
  onProgramClick,
  submittedResults = [],
  governSubmittedClick = false,
  title,
  subtitle,
  disclosureTitle,
  disclosureContent,
}) => {
  if (loading) {
    return (
      <Flex
        asChild
        align='center'
        justify='center'
        gap='xxs'
        className='h-full min-h-[45vh]'
      >
        <section>
          <Heading variant='h4'>Loading...</Heading>
          <LoadingDots color='primary' label='Loading school recommendations' />
        </section>
      </Flex>
    );
  }

  const cards = results?.slice(0, 2) ?? [];

  return (
    <Flex
      asChild
      gap='lg'
      className='rounded-lg border border-neutral-200 bg-secondary-50 p-lg'
    >
      <section>
        <Flex asChild gap='xxs'>
          <header>
            <Heading variant='h3'>
              {title ??
                'Great News! You\u2019re a Strong Match for These Schools!'}
            </Heading>
            <Text variant='b4'>
              {subtitle ?? 'Select a school to request more information.'}
            </Text>
          </header>
        </Flex>

        <Flex direction='column' direction-md='row' gap='lg'>
          {cards.map((result, index) => {
            const isProgramSubmitted = submittedResults.some(
              (submission) => submission.programId === result.programId,
            );
            return (
              <RecommenderCard
                key={result.programId ?? `${result.schoolSlug}-${index}`}
                result={result}
                position={index + 1}
                isProgramSubmitted={isProgramSubmitted}
                governSubmittedClick={governSubmittedClick}
                onProductClick={(corr) =>
                  onProgramClick({ ...result, ...corr })
                }
              />
            );
          })}
        </Flex>

        <Flex
          asChild
          direction='row'
          align='center'
          justify='between'
          gap='none'
        >
          <footer>
            <SponsoredResultsPopover
              position='above'
              spanText='Learn More About Our Partners'
              disclosureTitle={disclosureTitle}
              disclosureContent={disclosureContent}
            />
            {onCloseExperience && (
              <Button
                type='button'
                variant='textOnlyNeutral'
                size='sm'
                iconAfter='chevron-right'
                onClick={onCloseExperience}
              >
                Skip to all results
              </Button>
            )}
          </footer>
        </Flex>
      </section>
    </Flex>
  );
};

export default ProgramSelect;
